import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Target, 
  Eye, 
  Users, 
  Shield, 
  Zap,
  Mail,
  MapPin,
  Phone,
  Send,
  ArrowLeft,
  CheckCircle2,
  Globe,
  Clock
} from "lucide-react";
import { toast } from "sonner";

export default function About() {
  const navigate = useNavigate();
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [sending, setSending] = useState(false);

  const handleContact = async (e) => {
    e.preventDefault();
    setSending(true);
    
    // Simulate sending (mock)
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast.success("Message envoyé ! Nous vous répondrons sous 24h.");
    setContactForm({ name: '', email: '', company: '', message: '' });
    setSending(false);
  };

  const values = [
    {
      icon: <Target className="w-6 h-6" />,
      title: "Notre Mission",
      description: "Démocratiser l'accès aux entretiens de qualité en automatisant la pré-qualification grâce à l'IA, permettant aux recruteurs de se concentrer sur l'humain."
    },
    {
      icon: <Eye className="w-6 h-6" />,
      title: "Notre Vision",
      description: "Un monde où chaque candidat bénéficie d'une évaluation objective et où chaque entreprise trouve les talents qui lui correspondent vraiment."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Nos Valeurs",
      description: "Transparence, équité, innovation responsable. Nous croyons en une IA au service de l'humain, jamais à son remplacement."
    }
  ];

  const objectives = [
    "Réduire de 80% le temps de pré-qualification des candidats",
    "Éliminer les biais inconscients dans le processus de recrutement",
    "Offrir une expérience candidat disponible 24/7",
    "Garantir une traçabilité complète et conforme RGPD",
    "Permettre aux RH de se concentrer sur les entretiens finaux"
  ];

  const stats = [
    { value: "10,000+", label: "Entretiens réalisés" },
    { value: "500+", label: "Entreprises clientes" },
    { value: "85%", label: "Temps économisé" },
    { value: "4.8/5", label: "Satisfaction client" }
  ];

  return (
    <div className="min-h-screen bg-canvas">
      {/* Navigation */}
      <nav className="sticky-header px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div 
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => navigate('/')}
          >
            <img 
              src="/logo-ema.svg" 
              alt="Ema Logo" 
              className="h-16 w-auto"
            />
          </div>
          
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost"
              onClick={() => navigate('/')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="px-6 py-16 lg:py-24">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-display font-bold text-4xl lg:text-5xl text-ink mb-6">
            Ema - Plateforme IA d'entretiens RH autonome
          </h1>
          <p className="text-lg text-ink-muted max-w-2xl mx-auto">
            Ema est une plateforme IA d'entretiens RH autonome, développée par l'entreprise Proprio Technologie.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="px-6 py-12 bg-brand-primary">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="font-display font-bold text-3xl lg:text-4xl text-brand-accent mb-1">
                  {stat.value}
                </p>
                <p className="text-white/80 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission / Vision / Values */}
      <section className="px-6 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {values.map((item, index) => (
              <Card key={index} className="card-flat">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-brand-accent/20 rounded-lg flex items-center justify-center mb-6">
                    <div className="text-brand-primary">{item.icon}</div>
                  </div>
                  <h3 className="font-display font-semibold text-xl text-ink mb-3">
                    {item.title}
                  </h3>
                  <p className="text-ink-muted">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Objectives */}
      <section className="px-6 py-16 bg-canvas-paper">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-bold text-3xl text-ink text-center mb-12">
            Nos Objectifs
          </h2>
          <div className="space-y-4">
            {objectives.map((objective, index) => (
              <div 
                key={index}
                className="flex items-center gap-4 bg-white p-4 rounded-lg border border-zinc-200"
              >
                <div className="w-10 h-10 bg-functional-success/20 rounded-full flex items-center justify-center shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-functional-success" />
                </div>
                <p className="text-ink">{objective}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Ema */}
      <section className="px-6 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-display font-bold text-3xl text-ink text-center mb-12">
            Pourquoi Ema ?
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <Zap />, title: "Rapidité", desc: "Entretiens disponibles 24/7, résultats instantanés" },
              { icon: <Shield />, title: "Objectivité", desc: "Évaluation sans biais, scoring standardisé" },
              { icon: <Globe />, title: "Accessibilité", desc: "Multilingue, accessible partout dans le monde" },
              { icon: <Clock />, title: "Flexibilité", desc: "Les candidats passent l'entretien quand ils veulent" }
            ].map((item, index) => (
              <div key={index} className="text-center p-6">
                <div className="w-12 h-12 bg-brand-accent/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <div className="text-brand-primary">{item.icon}</div>
                </div>
                <h3 className="font-semibold text-ink mb-2">{item.title}</h3>
                <p className="text-sm text-ink-muted">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="px-6 py-16 bg-canvas-paper">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="font-display font-bold text-3xl text-ink mb-6">
                Contactez-nous
              </h2>
              <p className="text-ink-muted mb-8">
                Une question ? Un projet ? Notre équipe est là pour vous accompagner 
                dans la transformation de votre processus de recrutement.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-accent/20 rounded-lg flex items-center justify-center">
                    <Mail className="w-5 h-5 text-brand-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-ink-muted">Email</p>
                    <p className="font-medium text-ink">contact@ema.io</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-accent/20 rounded-lg flex items-center justify-center">
                    <Phone className="w-5 h-5 text-brand-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-ink-muted">Téléphone</p>
                    <p className="font-medium text-ink">+33 1 23 45 67 89</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-accent/20 rounded-lg flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-brand-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-ink-muted">Adresse</p>
                    <p className="font-medium text-ink">Paris, France</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <Card className="card-flat">
              <CardContent className="p-6">
                <form onSubmit={handleContact} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nom *</Label>
                      <Input
                        id="name"
                        value={contactForm.name}
                        onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Votre nom"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="votre@email.com"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="company">Entreprise</Label>
                    <Input
                      id="company"
                      value={contactForm.company}
                      onChange={(e) => setContactForm(prev => ({ ...prev, company: e.target.value }))}
                      placeholder="Nom de votre entreprise"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      value={contactForm.message}
                      onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                      placeholder="Comment pouvons-nous vous aider ?"
                      rows={5}
                      required
                    />
                  </div>
                  
                  <Button type="submit" className="w-full btn-primary" disabled={sending}>
                    {sending ? (
                      "Envoi en cours..."
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Envoyer
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-12 border-t border-zinc-200">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <img 
              src="/logo-ema.svg" 
              alt="Ema Logo" 
              className="h-12 w-auto"
            />
          </div>
          
          <p className="text-sm text-ink-muted">
            © 2026 Ema. Tous droits réservés. Conforme RGPD.
          </p>
          
          <div className="flex items-center gap-6">
            <a href="#" className="text-sm text-ink-muted hover:text-ink">CGU</a>
            <a href="#" className="text-sm text-ink-muted hover:text-ink">Confidentialité</a>
            <a href="#" className="text-sm text-ink-muted hover:text-ink">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
